import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { appPermissions } from '@canvas/permissions/singleton';
import { captureException } from '@canvas/sentry-core';

import { currentPermissions } from './permissions-state';

export type InvokeParams<T extends (...args: any[]) => any> = {
  tag: string;
  isEnabled?: boolean;
  invokeV1: T;
  invokeV2: T;
  compareResults?: boolean;
  compareMethod?: (a: ReturnType<T>, b: ReturnType<T>) => boolean;
  useResultOf?: 'v1' | 'v2';
  args?: Parameters<T>;
};

export function useVersionedHook<T extends (...args: any[]) => any>(
  params: InvokeParams<T>
) {
  return invokeVersioned(params);
}

export function invokeVersioned<T extends (...args: any[]) => any>({
  tag,
  isEnabled = isFeatureFlagEnabled('v2Permissions'),
  invokeV1,
  invokeV2,
  compareResults = false,
  compareMethod = (a, b) => a === b,
  useResultOf = isFeatureFlagEnabled('v2PermissionsReads') ? 'v2' : 'v1',
  args = [] as unknown as Parameters<T>,
}: InvokeParams<T>) {
  if (!isEnabled) {
    return invokeV1(...args);
  }

  try {
    const resultV1 = invokeV1(...args);
    const resultV2 = invokeV2(...args);

    if (compareResults) {
      const isSame = compareMethod(resultV1, resultV2);

      if (!isSame) {
        const error = new Error(`v1 and v2 do not match: ${tag}`);
        console.error(error, { resultV1, resultV2 });

        captureException(error, {
          tags: {
            captureLocation: 'invoked-versioned-comparison-v2',
            versioned: tag,
            v1: resultV1,
            v2: resultV2,
            claimsV1: Array.from(currentPermissions.boardAccess.values()).join('|'),
            claimsV2: Array.from(appPermissions.claims.values()).join('|'),
            policies: appPermissions.policies.summarise(),
          },
        });
        return resultV1;
      }
    }

    if (useResultOf === 'v1') {
      return resultV1;
    } else {
      return resultV2;
    }
  } catch (err) {
    captureException(err, {
      tags: {
        captureLocation: 'invoked-versioned-fatal',
        versioned: tag,
        policies: appPermissions.policies.summarise(),
      },
    });
    return invokeV1(...args);
  }
}
